import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            try {
              const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))
              // check for previous reload time not to reload infinitely
              if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
                window.sessionStorage.setItem('nuxt-reload', timeNow)
                window.location.reload(true /* skip cache */)
              }
            } catch {
              // don't throw an error if we have issues reading sessionStorage
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"ATTRAQT_KEY_FR":"625ec431646e0fb61742ebae","ATTRAQT_KEY_IT":"625ec43454e59f409ea5f804","ATTRAQT_KEY_DE":"66c358894430aeef9fc2407b","ATTRAQT_KEY_EN":"66c3588c7b21f6ec09b8b96f","ATTRAQT_KEY_ES":"66c3588c4430aeef9fc24080","ATTRAQT_KEY_FR_PRODUCT_PAGE":"64b650e2e8b9eebd1eb3e0bc","ATTRAQT_KEY_IT_PRODUCT_PAGE":"664df2a78e81b09e55342463","ATTRAQT_KEY_DE_PRODUCT_PAGE":"67a07d1686798a00d614be10","ATTRAQT_KEY_EN_PRODUCT_PAGE":"67a07aabeb3c9bad42299f69","ATTRAQT_KEY_ES_PRODUCT_PAGE":"67a07b5cc1cf66f3af6db940","ATTRAQT_API_URL":"https://api-eu.attraqt.io","TRIPETTO_TOKEN_DEV":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiOHdJYW1YVk5rVnZ2MWtDb2RNeGVjNXE1bVpaZC9NZk9IQ2tTbjNDZHlJST0iLCJkZWZpbml0aW9uIjoiS2tHNFZCQ3h6OWVsUXNyYnR0NE9uMHpmZmJtcXlUZ2dGWVhUVTlHVVA5dz0iLCJ0eXBlIjoiY29sbGVjdCJ9.MV-vS2RBX-oh5a3fGKFfCoqUbQ3lu2ui1s-6YMuufjM","TRIPETTO_TOKEN_IT":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiOHdJYW1YVk5rVnZ2MWtDb2RNeGVjNXE1bVpaZC9NZk9IQ2tTbjNDZHlJST0iLCJkZWZpbml0aW9uIjoiS2tHNFZCQ3h6OWVsUXNyYnR0NE9uMHpmZmJtcXlUZ2dGWVhUVTlHVVA5dz0iLCJ0eXBlIjoiY29sbGVjdCJ9.MV-vS2RBX-oh5a3fGKFfCoqUbQ3lu2ui1s-6YMuufjM","TRIPETTO_TOKEN_FR":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiOHdJYW1YVk5rVnZ2MWtDb2RNeGVjNXE1bVpaZC9NZk9IQ2tTbjNDZHlJST0iLCJkZWZpbml0aW9uIjoidXpYdHdBUWNzdzVtRW5sb0NYTE9JSGZESlFsMmpTdlNBSWl4bm51YzArWT0iLCJ0eXBlIjoiY29sbGVjdCJ9.CX9mFzTL6YYrR68BlAuRm8aAr3Z9Yb4feuEOSYlQ5gU","TRIPETTO_TOKEN_EN":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiOHdJYW1YVk5rVnZ2MWtDb2RNeGVjNXE1bVpaZC9NZk9IQ2tTbjNDZHlJST0iLCJkZWZpbml0aW9uIjoiRVh1WEVXRFZmUzRaVzhoVUxRbkNJZzN3TlZJT0k2dS9xbTRrWi93elVKTT0iLCJ0eXBlIjoiY29sbGVjdCJ9.W029C1rP9wHdVPYMTGEBX6iQzsfmiSjzhOTHuBPogw0","TRIPETTO_TOKEN_DE":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiOHdJYW1YVk5rVnZ2MWtDb2RNeGVjNXE1bVpaZC9NZk9IQ2tTbjNDZHlJST0iLCJkZWZpbml0aW9uIjoiY1pNd0I0QlcrdStKU0F2eXUxY1hYMm1tRW5Md2wydkRZSnRLR091bUkvZz0iLCJ0eXBlIjoiY29sbGVjdCJ9.lLIQYOx_1q_qHPfh3XQ5tX6MQFC9LhSPPimyltnYIBc","TRIPETTO_TOKEN_ES":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiOHdJYW1YVk5rVnZ2MWtDb2RNeGVjNXE1bVpaZC9NZk9IQ2tTbjNDZHlJST0iLCJkZWZpbml0aW9uIjoiNWJQbnA0NXZNTmdhdWVLcTI5M0xIdGhLWXA1VUI5RjIyd3dtcnRmbk95VT0iLCJ0eXBlIjoiY29sbGVjdCJ9.ItKu4K1rvSw3viOvsBO8FZBZ2kp5WW6QLqsqZ6qX6ow","TRIPETTO_TOKEN_SKIN_CARE_FR_DEV":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiOHdJYW1YVk5rVnZ2MWtDb2RNeGVjNXE1bVpaZC9NZk9IQ2tTbjNDZHlJST0iLCJkZWZpbml0aW9uIjoid1E4dWJPQXNlVlFlQzQ1UGUxYTlHbnBYSG8wUFF1Z0ZIYUk2eTlwQW5Eaz0iLCJ0eXBlIjoiY29sbGVjdCJ9.DDyjV20mFZtSOjFm-h-bsYhw85VP-tQ0LvH-NAkZFQQ","TRIPETTO_TOKEN_SKIN_CARE_FR":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiOHdJYW1YVk5rVnZ2MWtDb2RNeGVjNXE1bVpaZC9NZk9IQ2tTbjNDZHlJST0iLCJkZWZpbml0aW9uIjoid1E4dWJPQXNlVlFlQzQ1UGUxYTlHbnBYSG8wUFF1Z0ZIYUk2eTlwQW5Eaz0iLCJ0eXBlIjoiY29sbGVjdCJ9.DDyjV20mFZtSOjFm-h-bsYhw85VP-tQ0LvH-NAkZFQQ","TRIPETTO_TOKEN_SKIN_CARE_IT":"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiOHdJYW1YVk5rVnZ2MWtDb2RNeGVjNXE1bVpaZC9NZk9IQ2tTbjNDZHlJST0iLCJkZWZpbml0aW9uIjoiYXEzNHJ0NklDdDRNMVNHR1AwMTdHQWRGR0h4emZXdEZDbUNOeG85dEVZWT0iLCJ0eXBlIjoiY29sbGVjdCJ9.UN6HdkWQCzUPaG3Lb8lWhHaG3iP8ONUBjwNdiLnbuRs","UP_SELL_FR":"6419712a5fa4f69970523abd","UP_SELL_IT":"6405ed2d0d7daff59ca9b59f","UP_SELL_DE":"67a07f06c1cf66f3af6db957","UP_SELL_ES":"67a07f3deb3c9bad42299f8c","UP_SELL_EN":"67a07fd3804bdc52f611b084","KLAVIYO_TOKEN_FR":"S4mZqV","KLAVIYO_TOKEN_IT":"XFepPj","KLAVIYO_TOKEN_EN":"QWzFT3","KLAVIYO_TOKEN_DE":"Y83XTw","KLAVIYO_TOKEN_ES":"YAKLBW","KLAVIYO_SUBSCRIBE_FORM_FR":"WFgzLB","KLAVIYO_SUBSCRIBE_FORM_IT":"UFyiU4","KLAVIYO_SUBSCRIBE_FORM_EN":"X98Zb9","KLAVIYO_SUBSCRIBE_FORM_DE":"RDrQgg","KLAVIYO_SUBSCRIBE_FORM_ES":"ShxCN5","AVIS_VERIFIES_TOKEN_IT":"a02cd4b5-4a33-b9e4-c193-965b64655ada","AVIS_VERIFIES_TOKEN_FR":"4ee763a5-657d-7844-d52d-da2a59c5b3ac","AVIS_VERIFIES_TOKEN_EN":"2b7c399a-6ca3-74c4-61e6-4022446499b9","AVIS_VERIFIES_TOKEN_DE":"fa551603-c628-9644-197e-f89c985f637e","AVIS_VERIFIES_TOKEN_ES":"f7e6a72c-7348-fda4-0512-9ff51d90a0d5","AVIS_VERIFIES_WIDGETS_TOKEN":"5y2e0tsj2387LnXAj","AVIS_VERIFIES_API_URL":"https://api-product-reviews.rr.skeepers.io/v1/websites","CURRENT_ENV":"prod","BOTMIND_CHAT_TOKEN":"7f0b31bf-5898-4ec2-b430-69c80b236612","DOMAIN":"https://www.aroma-zone.com","BOOXI_SHOP_ID_FR":"LEU4v89vKnm9DCUK3l6TB0yYYp4tsF93","BOOXI_SHOP_ID_BE":"LnM83rGre72x9sM6023u88F5b55Gl048","AZ_API_SUPPLY_URL":"https://api.aroma-zone.com","ADYEN_ENVIRONMENT":"LIVE","ADYEN_CLIENT_KEY":"live_7NJPSU3W4NA2HPFDFN3W23JU7IFJ4VRB","CUSTOMER_GROUP_WITHOUT_VAT":"ue_vat_intracom","LOCALE_EN_ENABLED":true,"LOCALE_DE_ENABLED":true,"LOCALE_ES_ENABLED":true,"SEO_EN_ENABLED":false,"SEO_DE_ENABLED":false,"SEO_ES_ENABLED":false,"CUSTOMER_GROUP_COOKIE":"customer_group","BUYAPOWA_URL":"https://bp.aroma-zone.com","BUYAPOWA_MARKET":"aromazonefr","BUYAPOWA_CAMPAIGN":"aromazonefr_raf1","ALGOLIA_APP_ID":"0A45RPRA7L","ALGOLIA_APP_KEY":"80fe9ee6877d6f8dbdd6f0e88705cab2"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  if (context.error) {
    app.context.error = context.error
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext, renderState) {
  if (!promises.length || appContext._redirected || appContext._errored || (renderState && renderState.aborted)) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext, renderState)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
